/**
 * 🕺 RpmAvatarCard — Ready Player Me opt-in 3D avatar
 *
 * Owner canon Feb 13, 2026: "Layer a real rigged 3D twin on top of
 * the photorealistic body-twin so your likeness actually walks around
 * Arena and Miko. Build this — I'll add the closet later."
 *
 * Flow:
 *   1. Card is COLLAPSED by default (nothing loaded, nothing rendered)
 *   2. User taps "Enable 3D Twin" → we mount the RPM iframe
 *   3. User completes RPM face-scan + customization → RPM postMessages
 *      the `.glb` URL to us
 *   4. We validate + save via /api/hero/rpm-avatar
 *   5. Iframe unmounts; Three.js viewer mounts with the saved model
 *   6. "Change avatar" reopens iframe; "Disable" clears everything
 *
 * Fallback flow (for in-app browsers that block the iframe): the user
 * can build their avatar at readyplayer.me and paste the .glb URL back
 * into the manual field.
 *
 * Accessibility: no 3D bundle, no iframe, no camera prompt, no
 * network traffic until the user explicitly opts in.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RPM_SUBDOMAIN = process.env.REACT_APP_RPM_SUBDOMAIN || "demo";
const RPM_ORIGIN    = `https://${RPM_SUBDOMAIN}.readyplayer.me`;
const RPM_FRAME_URL = `${RPM_ORIGIN}/avatar?frameApi&bodyType=fullbody`;

export default function RpmAvatarCard() {
  const [rpm,        setRpm]        = useState(null);       // saved record from server
  const [enabled,    setEnabled]    = useState(false);      // did user opt in?
  const [creating,   setCreating]   = useState(false);      // iframe mounted?
  const [saving,     setSaving]     = useState(false);
  const [Viewer,     setViewer]     = useState(null);       // lazy-loaded 3D viewer
  const [manualUrl,  setManualUrl]  = useState("");         // paste-URL fallback
  const frameRef = useRef(null);

  // ── Fetch saved avatar ─────────────────────────────────────
  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/hero/rpm-avatar`);
      setRpm(data?.rpm_avatar || null);
      if (data?.rpm_avatar?.url) setEnabled(true);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { refresh(); }, []);

  // ── Lazy-load 3D viewer ONLY when we actually have a URL to show ──
  useEffect(() => {
    if (!rpm?.url) { setViewer(null); return; }
    let active = true;
    import("./RpmAvatarViewer.jsx").then((m) => {
      if (active) setViewer(() => m.default);
    });
    return () => { active = false; };
  }, [rpm?.url]);

  // ── RPM postMessage listener (mounted only while creating) ────
  useEffect(() => {
    if (!creating) return;
    const onMessage = (e) => {
      if (e.origin !== RPM_ORIGIN) return;
      let msg;
      try {
        msg = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
      } catch { return; }
      if (msg?.source !== "readyplayerme") return;

      if (msg.eventName === "v1.frame.ready") {
        // subscribe to all v1.* events so we catch the export
        frameRef.current?.contentWindow?.postMessage(
          JSON.stringify({
            target:    "readyplayerme",
            type:      "subscribe",
            eventName: "v1.**",
          }),
          RPM_ORIGIN,
        );
      }
      if (msg.eventName === "v1.avatar.exported") {
        const url = msg?.data?.url;
        if (!url || !url.startsWith("https://models.readyplayer.me/") ||
            !url.toLowerCase().includes(".glb")) {
          toast.error("Ready Player Me returned an unexpected avatar URL.");
          return;
        }
        saveAvatar(url);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [creating]);

  const saveAvatar = async (url) => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/hero/rpm-avatar`,
                                         { avatar_url: url });
      setRpm(data.rpm_avatar);
      setCreating(false);
      setManualUrl("");
      toast.success("3D Twin locked in — orbit around and see yourself.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save avatar.");
    } finally { setSaving(false); }
  };

  const handleManualSave = () => {
    const url = (manualUrl || "").trim();
    if (!url.startsWith("https://models.readyplayer.me/") || !url.toLowerCase().includes(".glb")) {
      toast.error("Paste the full .glb URL from readyplayer.me — must start with https://models.readyplayer.me/ and end in .glb");
      return;
    }
    saveAvatar(url);
  };

  // Escape hatch for in-app browsers (Facebook, Instagram, TikTok, LinkedIn…).
  // We can't force the OS browser to open, but we CAN:
  //   1) copy the current URL to clipboard so paste is trivial
  //   2) try window.open — some in-app browsers show an "Open in browser" prompt
  //   3) build platform-specific intent/x-safari deep-links as best-effort
  const openInSystemBrowser = async () => {
    const currentUrl = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(currentUrl);
        toast.success("Link copied — open Chrome or Safari and paste it in.");
      } else {
        toast.info("Copy this URL into Chrome or Safari to continue: " + currentUrl);
      }
    } catch { /* ignore clipboard failure */ }

    const ua = (navigator.userAgent || "").toLowerCase();
    try {
      if (/android/.test(ua)) {
        // Android intent — opens in Chrome when available
        const httpsUrl = currentUrl.replace(/^https?:\/\//, "");
        window.location.href =
          `intent://${httpsUrl}#Intent;scheme=https;package=com.android.chrome;end`;
        return;
      }
      if (/iphone|ipad|ipod/.test(ua)) {
        // iOS: Safari's x-web-search is the closest thing to "open in system Safari"
        window.location.href = "x-safari-" + currentUrl;
        return;
      }
    } catch { /* fall through to plain open */ }

    // Universal fallback — opens a new tab; some in-app browsers prompt to
    // "Open in default browser" here.
    window.open(currentUrl, "_blank", "noopener,noreferrer");
  };

  const disableRpm = async () => {
    if (!confirm("Disable your 3D Twin? Your saved avatar will be removed.")) return;
    try {
      await axios.delete(`${API}/hero/rpm-avatar`);
      setRpm(null); setEnabled(false); setCreating(false);
      toast.success("3D Twin disabled.");
    } catch (e) { toast.error("Couldn't disable."); }
  };

  // ── Collapsed opt-in card ─────────────────────────────────
  if (!enabled) {
    return (
      <div className="rounded-2xl border border-[#6D28D9]/30 bg-gradient-to-br from-[#0f0518] via-[#080310] to-[#1b0a24] p-4 mb-6"
           data-testid="rpm-avatar-card-collapsed">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="text-2xl">🕺</div>
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#A78BFA] font-bold">
                3D Twin (Ready Player Me)
              </div>
              <div className="text-[10px] text-neutral-500 leading-snug max-w-md">
                Optional rigged 3D avatar that walks around Arena and Miko.
                Nothing loads until you turn it on. Cosmetic closet coming later.
              </div>
            </div>
          </div>
          <button
            onClick={() => { setEnabled(true); setCreating(true); }}
            className="px-3 py-1.5 rounded-lg bg-gradient-to-br from-[#6D28D9] to-[#7C3AED] text-white text-[11px] font-bold hover:brightness-110"
            data-testid="rpm-enable-btn"
          >
            Enable 3D Twin
          </button>
        </div>
      </div>
    );
  }

  // ── Enabled + expanded card ───────────────────────────────
  return (
    <div className="rounded-2xl border border-[#6D28D9]/50 bg-gradient-to-br from-[#0f0518] via-[#080310] to-[#1b0a24] p-4 mb-6 shadow-[0_0_20px_rgba(109,40,217,0.15)]"
         data-testid="rpm-avatar-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="text-2xl">🕺</div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#A78BFA] font-bold">
              3D Twin (Ready Player Me)
            </div>
            <div className="text-[10px] text-neutral-500">
              Rigged glTF avatar · orbit to inspect · closet coming soon
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {rpm?.url && (
            <button
              onClick={() => setCreating(true)}
              className="text-[10px] px-2 py-1 rounded-lg bg-black/40 border border-[#6D28D9]/40 text-[#C4B5FD] hover:bg-black/60"
              data-testid="rpm-change-btn"
            >
              Change
            </button>
          )}
          <button
            onClick={disableRpm}
            className="text-[10px] px-2 py-1 rounded-lg bg-neutral-900 border border-neutral-800 text-neutral-500 hover:text-neutral-300"
            data-testid="rpm-disable-btn"
          >
            Disable
          </button>
        </div>
      </div>

      {/* Creator iframe + fallback save — only mounted while creating */}
      {creating && (
        <div className="rounded-xl overflow-hidden border border-[#6D28D9]/50 mb-3 bg-black"
             data-testid="rpm-iframe-wrap">
          <div className="p-3 bg-[#1b0a24] border-b border-[#6D28D9]/40 text-[11px] text-[#C4B5FD] leading-snug">
            <div className="font-bold text-[#A78BFA] mb-1">📖 How to save your 3D Twin</div>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Wait for the Ready Player Me editor to load below.</li>
              <li>Take a selfie or pick a body, customize outfit + face.</li>
              <li>Tap <b>Done / Use avatar</b> in the editor — your Twin auto-saves back here.</li>
            </ol>
            <div className="mt-2 text-[10px] text-neutral-400">
              Blank screen? Facebook / Instagram in-app browsers block the editor.
              Open the site in Chrome or Safari, or use the paste-URL option below.
            </div>
            <button
              type="button"
              onClick={openInSystemBrowser}
              className="mt-2 w-full sm:w-auto px-3 py-1.5 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 text-white text-[11px] font-bold hover:brightness-110 inline-flex items-center justify-center gap-1.5"
              data-testid="rpm-open-in-browser-btn"
            >
              🌐 Open in Chrome / Safari
            </button>
          </div>
          <iframe
            ref={frameRef}
            title="Create your Ready Player Me avatar"
            src={RPM_FRAME_URL}
            allow="camera *; microphone *; clipboard-write"
            style={{ width: "100%", height: 560, border: 0 }}
            data-testid="rpm-iframe"
          />
          {saving && (
            <div className="text-[11px] text-[#A78BFA] text-center py-2 bg-black/60">
              Saving your 3D Twin…
            </div>
          )}
          <div className="p-3 bg-black/60 border-t border-[#6D28D9]/30 space-y-2">
            <details className="text-[11px] text-[#C4B5FD]">
              <summary className="cursor-pointer font-bold text-[#A78BFA] py-1"
                       data-testid="rpm-manual-toggle">
                🔗 Editor blocked, or already have an avatar? Paste your .glb URL here.
              </summary>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                  type="url"
                  placeholder="https://models.readyplayer.me/xxxxxxxxxxxxxxxxxxxx.glb"
                  value={manualUrl}
                  onChange={(e) => setManualUrl(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded bg-black/60 border border-[#6D28D9]/40 text-[11px] text-white font-mono"
                  data-testid="rpm-manual-url-input"
                />
                <button
                  type="button"
                  onClick={handleManualSave}
                  disabled={saving}
                  className="px-3 py-1.5 rounded bg-gradient-to-br from-[#6D28D9] to-[#7C3AED] text-white text-[11px] font-bold hover:brightness-110 disabled:opacity-50"
                  data-testid="rpm-manual-save-btn"
                >
                  💾 Save Twin
                </button>
              </div>
              <div className="mt-1 text-[10px] text-neutral-500">
                Tip: Open{" "}
                <a
                  href="https://readyplayer.me/avatar"
                  target="_blank" rel="noreferrer"
                  className="underline text-[#A78BFA]"
                >
                  readyplayer.me/avatar
                </a>
                {" "}directly, build your avatar, tap the share/copy icon to grab the .glb URL, then paste it here.
              </div>
            </details>
            <button
              onClick={() => setCreating(false)}
              className="w-full py-1.5 rounded-lg bg-neutral-900 border border-neutral-800 text-[11px] text-neutral-300"
              data-testid="rpm-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Saved avatar viewer */}
      {!creating && rpm?.url && Viewer && (
        <Viewer url={rpm.url} />
      )}

      {!creating && rpm?.url && !Viewer && (
        <div className="text-center text-neutral-500 text-xs py-8"
             data-testid="rpm-viewer-loading">
          Loading 3D viewer…
        </div>
      )}

      {!creating && !rpm?.url && (
        <div className="text-center text-neutral-400 text-xs py-6"
             data-testid="rpm-empty">
          Enable to start — no avatar saved yet.
        </div>
      )}

      {rpm?.url && (
        <div className="text-[9px] text-neutral-600 mt-2 truncate" title={rpm.url}>
          {rpm.url}
        </div>
      )}
    </div>
  );
}
